import { useCallback, useMemo, useState } from 'react';

import { ComboBoxMenuItem } from '../combo-box/combo-box-menu-item';
import { FilterChip, FilterChipProps } from '../filter-chip/filter-chip';
import { MenuItem } from '../filter-combo-box/filter-combo-box.types';

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProperties {
  options: MultiSelectOption[];
  selected: MultiSelectOption[];
  onSelected: (selection: MenuItem) => void;
  width?: string;
}

const style = {
  container: 'flex flex-col shadow rounded overflow-hidden inline-block relative',
};

const MultiSelect = (props: MultiSelectProperties) => {
  const { options, onSelected, selected, width } = props;

  const menuItems = useMemo(() => {
    return options.map((option) => {
      const isSelected = selected.some((s) => s.value === option.value);
      return { ...option, isSelected };
    });
  }, [options, selected]);

  return (
    <div className={style.container} style={{ width: width, maxWidth: width }}>
      {menuItems.map((item) => {
        return <ComboBoxMenuItem key={item.value} item={item} onSelect={onSelected} isSelected={item.isSelected} />;
      })}
    </div>
  );
};

export interface MultiSelectDropdownProperties
  extends Omit<Partial<FilterChipProps>, 'menuContent' | 'numberSelected' | 'onClearSelection'> {
  options: MultiSelectOption[];
  onChange?: (selected: MultiSelectOption[]) => void;
  value: string;
  selected?: MultiSelectOption[];
}

export const MultiSelectDropdown = (props: MultiSelectDropdownProperties) => {
  const { options, onChange, value, width = '352px', isOpen = false, selected: defaultSelected = [], ...rest } = props;

  const [selected, setSelected] = useState<MultiSelectOption[]>(defaultSelected);

  const handleSelect = useCallback(
    (selection: MenuItem) => {
      const newSelected = [...selected];
      const index = newSelected.findIndex((s) => {
        return s.value === selection.value;
      });

      if (index === -1) newSelected.push({ label: selection.label, value: selection.value });
      else newSelected.splice(index, 1);

      if (onChange) onChange([...newSelected]);
      setSelected(newSelected);
    },
    [onChange, selected]
  );

  const onClearSelection = useCallback(() => {
    setSelected([]);
    if (onChange) onChange([]);
  }, [onChange]);

  const numberSelected = useMemo(() => {
    return selected.length;
  }, [selected]);

  const firstSelection = useMemo(() => {
    if (selected.length === 0) return;
    return [...selected.values()][0].label;
  }, [selected]);

  return (
    <FilterChip
      menuContent={<MultiSelect options={options} selected={selected} onSelected={handleSelect} width={width} />}
      numberSelected={numberSelected}
      onClearSelection={onClearSelection}
      value={value}
      firstSelection={firstSelection}
      width={width}
      isOpen={isOpen}
      {...rest}
    />
  );
};
